// packages/frontend/src/components/MoveModal.tsx
import { useState, useEffect } from 'react'
import { useFileStore } from '../stores/fileStore'
import { X, Move, Folder, ChevronRight, Home } from 'lucide-react'
import type { FileSystemItem } from '@shared'

interface MoveModalProps {
  isOpen: boolean
  onClose: () => void
  items: FileSystemItem[]
}

interface FolderNode {
  id: string
  name: string
  path: string
  children: FolderNode[]
  isExpanded: boolean
}

export function MoveModal({ isOpen, onClose, items }: MoveModalProps) {
  const [selectedPath, setSelectedPath] = useState('')
  const [isMoving, setIsMoving] = useState(false)
  const [folderTree, setFolderTree] = useState<FolderNode[]>([])
  const [error, setError] = useState('')
  const { moveItems, loadFolderStructure } = useFileStore()

  useEffect(() => {
    if (isOpen) {
      loadFolderStructure().then(folders => {
        // Build folder tree structure
        const tree = buildFolderTree(folders)
        setFolderTree(tree)
        setSelectedPath('')
        setError('')
      })
    }
  }, [isOpen, loadFolderStructure])

  const buildFolderTree = (folders: any[]): FolderNode[] => {
    const folderMap = new Map<string, FolderNode>()
    const rootFolders: FolderNode[] = []

    // Create all folder nodes
    folders.forEach(folder => {
      folderMap.set(folder.id, {
        id: folder.id,
        name: folder.name,
        path: folder.path,
        children: [],
        isExpanded: false
      })
    })

    // Build tree structure
    folders.forEach(folder => {
      const node = folderMap.get(folder.id)!
      if (folder.parentFolderId) {
        const parent = folderMap.get(folder.parentFolderId)
        if (parent) {
          parent.children.push(node)
        }
      } else {
        rootFolders.push(node)
      }
    })

    return rootFolders
  }

  const toggleFolder = (folderId: string) => {
    const updateTree = (nodes: FolderNode[]): FolderNode[] => {
      return nodes.map(node => {
        if (node.id === folderId) {
          return { ...node, isExpanded: !node.isExpanded }
        }
        return { ...node, children: updateTree(node.children) }
      })
    }
    setFolderTree(updateTree(folderTree))
  }

  const renderFolderTree = (nodes: FolderNode[], depth = 0) => {
    return nodes.map(node => (
      <div key={node.id}>
        <button
          onClick={() => setSelectedPath(node.path)}
          className={`flex w-full items-center rounded-lg p-2 text-left hover:bg-dark-700 ${
            selectedPath === node.path ? 'bg-primary-500/20 text-primary-400' : 'text-dark-200'
          }`}
          style={{ paddingLeft: `${0.5 + depth * 1.5}rem` }}
        >
          {node.children.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                toggleFolder(node.id)
              }}
              className="mr-1 flex h-5 w-5 items-center justify-center"
            >
              <ChevronRight 
                className={`h-3 w-3 transition-transform ${
                  node.isExpanded ? 'rotate-90' : ''
                }`} 
              />
            </button>
          )}
          <Folder className="mr-2 h-4 w-4" />
          <span className="truncate">{node.name}</span>
        </button>
        {node.isExpanded && node.children.length > 0 && (
          <div>
            {renderFolderTree(node.children, depth + 1)}
          </div>
        )}
      </div>
    ))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (items.length === 0) return

    setIsMoving(true)
    setError('')

    try {
      await moveItems(items.map(item => ({
        id: item.item.id,
        type: item.type as 'file' | 'folder'
      })), selectedPath)
      
      onClose()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Move failed')
    } finally {
      setIsMoving(false)
    }
  }

  const handleClose = () => {
    if (!isMoving) {
      onClose()
      setSelectedPath('')
      setError('')
    }
  }

  if (!isOpen || items.length === 0) return null

  const fileCount = items.filter(item => item.type === 'file').length
  const folderCount = items.filter(item => item.type === 'folder').length

  const getItemsSummary = () => {
    const parts = []
    if (fileCount > 0) parts.push(`${fileCount} file${fileCount !== 1 ? 's' : ''}`)
    if (folderCount > 0) parts.push(`${folderCount} folder${folderCount !== 1 ? 's' : ''}`)
    return parts.join(' and ')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl bg-dark-800 p-6 shadow-2xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-purple-500/20 p-2">
              <Move className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Move Items</h2>
              <p className="text-dark-400">
                Moving {getItemsSummary()}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isMoving}
            className="rounded-lg p-2 text-dark-400 hover:bg-dark-700 hover:text-white disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Destination Selection */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-200">
              Select Destination
            </label>
            
            {/* Root folder option */}
            <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-dark-600 bg-dark-900/50 p-2">
              <button
                type="button"
                onClick={() => setSelectedPath('')}
                className={`flex w-full items-center rounded-lg p-2 text-left hover:bg-dark-700 ${
                  selectedPath === '' ? 'bg-primary-500/20 text-primary-400' : 'text-dark-200'
                }`}
              >
                <Home className="mr-2 h-4 w-4" />
                <span>Root Folder</span>
              </button>
              
              {/* Folder tree */}
              {renderFolderTree(folderTree)}
            </div>

            {selectedPath && (
              <p className="mt-2 text-sm text-dark-400">
                Moving to: <span className="text-white">{selectedPath || 'Root folder'}</span>
              </p>
            )}

            {error && (
              <p className="mt-2 text-sm text-red-400">{error}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isMoving}
              className="btn btn-ghost disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isMoving}
              className="btn btn-primary disabled:opacity-50"
            >
              {isMoving ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Moving...
                </>
              ) : (
                <>
                  <Move className="mr-2 h-4 w-4" />
                  Move Here
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}